import moment from "moment";
import { useState, useEffect } from "react";
import { ComponentStatus } from "../../components";
import { errorModal } from "../../components/Alerts";
import { JumpBtn } from "../../components/Buttons";
import { HorizontalCard } from "../../components/Card";
import {
    BackImgContainer,
    ScrollContainer,
    TransparentContainer,
} from "../../components/Containers";
import Modal from "../../components/Modal";
import { FavaloroSpinner } from "../../components/Spinner";
import { CustomerHistory } from "../../models";
import { CustomerService } from "../../services";
import { CommonConfig } from "../../shared/Constants";

const parseDate = (date?: Date) => {
    return date ? moment(date).format("DD/MM/YY HH:MM:SS") : "-";
};

interface Props {
    amountData: (item: CustomerHistory) => { value: string; Icon: JSX.Element };
    cancelOrder: (id: number) => Promise<boolean>;
}

const MobileView: React.FC<Props> = ({ amountData, cancelOrder }) => {
    const [status, setStatus] = useState<ComponentStatus>("complete");
    const [prevPage, setPrevPage] = useState<number>(0);
    const [page, setPage] = useState<number>(0);
    const [count, setCount] = useState<number>(0);
    const [histories, setHistories] = useState<CustomerHistory[]>([]);
    const [error, setError] = useState<string>("");
    const [forceFetch, setforceFetch] = useState<boolean>(false);

    const [openModal, setOpenModal] = useState(false);
    const [isAlertModal, setIsAlertModal] = useState(false);
    const [dataModal, setDataModal] = useState<CustomerHistory>();

    const fetchOrders = async (page: number, prevPage: number) => {
        setStatus("loading");
        const sr = await CustomerService.GetHistory(page);

        if (sr.status()) {
            if (prevPage === page) setHistories(sr.data.list);
            else setHistories((prev) => [...prev, ...sr.data.list]);
            setCount(sr.data.count);
            setStatus("complete");
        } else {
            setStatus("errored");
            setError(sr.error);
        }
    };

    useEffect(() => {
        fetchOrders(page, prevPage);
    }, [prevPage, page, forceFetch]);

    const clearModal = () => {
        setOpenModal(!openModal);
        setIsAlertModal(false);
        setDataModal(undefined);
    };

    const getModal = () => {
        if (isAlertModal) {
            const groupButtons = () => {
                return (
                    <>
                        <JumpBtn
                            key={1}
                            borderColor={"#0053a0"}
                            colorOnHover={"#0053a0"}
                            value="Si"
                            onClick={async () => {
                                if (await cancelOrder(dataModal?.orderID!)) {
                                    setforceFetch((prev) => !prev);
                                }
                                clearModal();
                            }}
                            style={{
                                padding: "5px 1.6em 0.65em",
                            }}
                        />
                        <JumpBtn
                            key={2}
                            borderColor={"#ef3c34"}
                            colorOnHover={"#ef3c34"}
                            value="No"
                            onClick={() => clearModal()}
                        />
                    </>
                );
            };
            return (
                <>
                    <Modal
                        title={"Confirma que desea cancelar su pedido?"}
                        active={openModal}
                        hideModal={() => clearModal()}
                        children={groupButtons()}
                    />
                </>
            );
        }

        const Details = () => {
            return (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-around",
                    }}
                >
                    <span>Menú: {dataModal?.orderMenuDescription}</span>
                    <span>Tipo de Menú: {dataModal?.orderMenuTypeDescription}</span>
                    <span>Creditos: {dataModal?.amount}</span>
                    <span>
                        {dataModal?.orderStatusTypeDescription}: {parseDate(dataModal?.orderEditedDate)}
                    </span>
                    <span>Envio a Piso: {dataModal?.orderIsDelivery ? 'Si' : 'No'}</span>
                    <span>{dataModal?.orderDeliveryMessage}</span>
                </div>
            );
        };

        return (
            <>
                <Modal
                    title={"Detalles del Pedido"}
                    active={openModal}
                    hideModal={() => clearModal()}
                    children={Details()}
                    footer={
                        <JumpBtn
                            key={1}
                            borderColor={"black"}
                            colorOnHover={"rgba(11, 2, 2, 0.44)"}
                            value="Cerrar"
                            onClick={() => clearModal()}
                        />
                    }
                />
            </>
        );
    };

    const getActions = (history: CustomerHistory) => {
        if (history.balanceEventTypeCode?.toUpperCase() !== "ORDER")
            return undefined;
        if (history.orderStatusTypeCode?.toUpperCase() === "PENDING") {
            return [
                <JumpBtn
                    key={0}
                    borderColor={"#0053a0"}
                    colorOnHover={"#0053a0"}
                    value="Detalles"
                    onClick={() => {
                        setDataModal(history);
                        setOpenModal(true);
                    }}
                    style={{
                        padding: "5px 1.6em 0.65em",
                    }}
                />,
                <JumpBtn
                    key={1}
                    borderColor={"#ef3c34"}
                    colorOnHover={"#ef3c34"}
                    value="Cancelar"
                    onClick={async () => {
                        setDataModal(history);
                        setIsAlertModal(true);
                        setOpenModal(true);
                    }}
                    style={{
                        padding: "5px 1.6em 0.65em",
                    }}
                />,
            ];
        }

        return [
            <JumpBtn
                key={0}
                borderColor={"black"}
                colorOnHover={"black"}
                value="Detalles"
                onClick={() => {
                    setDataModal(history);
                    setOpenModal(true);
                }}
                style={{
                    padding: "5px 5.6em 0.65em",
                    backgroundColor: "white",
                }}
            />,
        ];
    };

    const getExtraText = (history: CustomerHistory) => {
        return history.balanceEventTypeCode.toUpperCase() === "ORDER"
            ? `Estado: ${history.orderStatusTypeDescription}`
            : undefined;
    };

    const getTitle = (history: CustomerHistory) => {
        return history.balanceEventTypeCode.toUpperCase() === "ORDER"
            ? `Pedido: ${history.orderMenuDescription}`
            : history.balanceEventTypeDescription!;
    };

    const items = histories?.map((history, index) => {
        const amount = amountData(history);
        return (
            <HorizontalCard
                key={index}
                title={getTitle(history)}
                date={parseDate(history.eventDate)}
                description={amount.value}
                extraText={getExtraText(history)}
                icon={amount.Icon}
                customGroup={getActions(history)}
            />
        );
    });

    const render = () => {
        switch (status) {
            case "loading":
                return <FavaloroSpinner />;
            case "complete":
                let pages = Math.floor(count / CommonConfig.ITEMS_PER_PAGE);

                return (
                    <>
                        {getModal()}
                        <TransparentContainer>
                            <ScrollContainer>{items}</ScrollContainer>
                            <JumpBtn
                                borderColor={page === pages ? "#4e4e4e" : "#0053a0"}
                                colorOnHover={page === pages ? "#4e4e4e" : "#0053a0"}
                                backgroundColor={page === pages ? "#4e4e4e" :"#0053a0"}
                                value="Cargar Mas..."
                                onClick={() => {
                                    setPrevPage(page);
                                    setPage((prev) => prev + 1);
                                }}
                                style={{
                                    fontSize: "65%",
                                    color: page === pages ? "black" : "white"
                                }}
                                disabled={page === pages}
                            />
                        </TransparentContainer>
                    </>
                );
            case "errored":
                return errorModal(error);
            default:
                return null;
        }
    };

    return <BackImgContainer>{render()}</BackImgContainer>;
};

export default MobileView;
